<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, provide, ref } from 'vue'
import { usePopup } from '../../composables/usePopup'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{
  trigger?: ('hover' | 'click')[]
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight'
  disabled?: boolean
  open?: boolean
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement
}>(), { trigger: () => ['hover'], placement: 'bottomLeft', disabled: undefined, open: undefined })
const emit = defineEmits<{ 'update:open': [open: boolean]; openChange: [open: boolean] }>()
const { disabled } = useFormControl(props)
const innerOpen = ref(false)
const mergedOpen = useBoundValue(props, 'open', innerOpen)
const isOpen = computed(() => !disabled.value && Boolean(mergedOpen.value))
const triggerRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
let hoverTimer: ReturnType<typeof setTimeout> | undefined
function setOpen(value: boolean) {
  if ((disabled.value && value) || value === isOpen.value) return
  innerOpen.value = value
  emit('update:open', value)
  emit('openChange', value)
}
const { container, style } = usePopup(isOpen, triggerRef, panelRef, { placement: () => props.placement, getPopupContainer: () => props.getPopupContainer, close: () => setOpen(false), onEscape: () => triggerRef.value?.querySelector<HTMLElement>('button,[tabindex]')?.focus() })
function clearHover() { if (hoverTimer) clearTimeout(hoverTimer); hoverTimer = undefined }
function handleMouseEnter() { clearHover(); if (props.trigger.includes('hover')) setOpen(true) }
function handleMouseLeave() { clearHover(); if (props.trigger.includes('hover')) hoverTimer = setTimeout(() => setOpen(false), 150) }
async function handleKeydown(event: KeyboardEvent) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  event.preventDefault()
  setOpen(true)
  await nextTick()
  const items = panelRef.value?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"]),button:not([disabled]),input:not([disabled])')
  if (items?.length) items[event.key === 'ArrowDown' ? 0 : items.length - 1].focus()
}
function handlePanelKeydown(event: KeyboardEvent) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  const items = Array.from(panelRef.value?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])') ?? [])
  if (!items.length) return
  event.preventDefault()
  const current = items.indexOf(document.activeElement as HTMLElement)
  items[(current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length].focus()
}
provide('rrDropdownClose', () => setOpen(false))
onBeforeUnmount(clearHover)
</script>

<template>
  <div ref="triggerRef" v-bind="$attrs" class="rr-dropdown" :aria-expanded="isOpen" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" @keydown="handleKeydown">
    <div class="rr-dropdown__trigger" @click.capture="props.trigger.includes('click') && setOpen(!isOpen)"><slot /></div>
  </div>
  <Teleport v-if="container" :to="container">
    <Transition name="rr-slide-up"><div v-if="isOpen" ref="panelRef" class="rr-dropdown__overlay" :style="style" @mouseenter="clearHover" @mouseleave="handleMouseLeave" @keydown="handlePanelKeydown"><slot name="overlay" /></div></Transition>
  </Teleport>
</template>

<style scoped>
.rr-dropdown {
  position: relative;
  display: inline-flex;
  box-sizing: border-box;
}

.rr-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.rr-dropdown__overlay {
  position: absolute;
  z-index: var(--rr-z-dropdown, 1050);
  min-width: 120px;
  background: var(--rr-color-surface, #ffffff);
  border: 1px solid var(--rr-color-border, #e1e5ea);
  border-radius: var(--rr-radius-panel, 8px);
  box-shadow: var(--rr-shadow-2, 0 4px 12px rgba(15, 23, 42, 0.1));
  padding: 4px;
  box-sizing: border-box;
}

</style>
